import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Activity, BadgeCheck, BarChart3, Check, Cpu, Gauge, Images, LayoutDashboard,
  LogIn, MessageCircle, Quote, Router as RouterIcon, Rocket, Shield, Smartphone, Star, Ticket, Users, Wifi, X, Zap,
} from "lucide-react";
import { api, rp, waLink } from "@/lib/api";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1572634683265-0b22479e6c59?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";
const RACK_IMG = "https://images.unsplash.com/photo-1661353657344-ec45eacbdbdb?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";
const DATA_IMG = "https://images.unsplash.com/photo-1606778303077-3780ea8d5420?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

const FEATURES = [
  { icon: RouterIcon, title: "REST API Mikrotik", desc: "Terhubung langsung ke RouterOS v7 lewat REST/API-SSL — hotspot, PPPoE, profil, dan trafik dalam satu dasbor.", tone: "from-cyan-500/20 to-cyan-500/0", ring: "text-cyan-300" },
  { icon: Ticket, title: "Voucher Hotspot", desc: "Generate massal, cetak kartu A4 atau struk thermal lengkap dengan QR auto-login, harga, dan durasi.", tone: "from-violet-500/20 to-violet-500/0", ring: "text-violet-300" },
  { icon: Wifi, title: "Pelanggan FTTH", desc: "Tagihan bulanan, kuitansi, auto-isolir, tunda isolir, dan riwayat pembayaran per pelanggan.", tone: "from-fuchsia-500/20 to-fuchsia-500/0", ring: "text-fuchsia-300" },
  { icon: Users, title: "Mitra & Reseller", desc: "Portal mitra dengan saldo, jual voucher, permintaan deposit, dan notifikasi real-time.", tone: "from-emerald-500/20 to-emerald-500/0", ring: "text-emerald-300" },
  { icon: BarChart3, title: "Laporan & Grafik", desc: "Pendapatan harian, bulanan, tren 12 bulan, laporan voucher, saldo, dan audit trail siap ekspor.", tone: "from-amber-500/20 to-amber-500/0", ring: "text-amber-300" },
  { icon: Smartphone, title: "Bisa Dipasang di HP", desc: "Tampilan responsif + PWA: pasang seperti aplikasi, terima notifikasi dan alarm permintaan saldo.", tone: "from-sky-500/20 to-sky-500/0", ring: "text-sky-300" },
];

const STEPS = [
  { n: "01", title: "Daftar Member", desc: "Isi data usaha, pilih paket sewa bulanan atau tahunan, lalu konfirmasi via WhatsApp." },
  { n: "02", title: "Hubungkan Router", desc: "Masukkan IP/DDNS, port, user & password router. Tes koneksi satu klik." },
  { n: "03", title: "Mulai Berjualan", desc: "Buat voucher, kelola pelanggan FTTH, pantau pendapatan real-time dari mana saja." },
];

const STATS = [
  { icon: Gauge, label: "Monitoring", value: "Real-time" },
  { icon: Zap, label: "Sinkron Router", value: "20 detik" },
  { icon: Shield, label: "Level Akses", value: "5 Role" },
  { icon: Activity, label: "Mode", value: "24/7 Online" },
];

function Blobs() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <div className="absolute -left-24 -top-32 h-[26rem] w-[26rem] rounded-full bg-cyan-500/20 blur-[120px]" />
      <div className="absolute -right-24 top-10 h-[24rem] w-[24rem] rounded-full bg-violet-600/25 blur-[120px]" />
      <div className="absolute bottom-0 left-1/3 h-[20rem] w-[20rem] rounded-full bg-fuchsia-600/15 blur-[130px]" />
      <div
        className="absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(148,163,184,0.25) 1px, transparent 1px), linear-gradient(to bottom, rgba(148,163,184,0.25) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage: "radial-gradient(ellipse 70% 60% at 50% 0%, black 40%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 0%, black 40%, transparent 100%)",
        }}
      />
    </div>
  );
}

function Brand({ logo, appName, size = "h-9 w-9", icon = "h-5 w-5" }) {
  if (logo) return <img src={logo} alt={appName} className={`${size} rounded-xl object-contain`} />;
  return (
    <div className={`grid ${size} place-items-center rounded-xl bg-gradient-to-br from-cyan-400 to-violet-600 shadow-lg shadow-violet-600/25`}>
      <RouterIcon className={`${icon} text-white`} />
    </div>
  );
}

/**
 * Rotasi nomor CS (round-robin per kunjungan halaman).
 * Dipanggil sekali per page-load; flag modul mencegah penambahan ganda akibat
 * React StrictMode yang menjalankan effect dua kali di mode pengembangan.
 */
let csTurnPicked = null;

function pickCsTurn() {
  if (csTurnPicked !== null) return csTurnPicked;
  try {
    const n = Number(localStorage.getItem("ab_cs_turn") || 0) || 0;
    localStorage.setItem("ab_cs_turn", String((n + 1) % 100000));
    csTurnPicked = n;
  } catch (e) {
    csTurnPicked = Math.floor(Math.random() * 1000);
  }
  return csTurnPicked;
}

export default function Landing() {
  const [info, setInfo] = useState(null);
  const [landing, setLanding] = useState(null);
  const [csTurn, setCsTurn] = useState(pickCsTurn);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/public/plans")
      .then(({ data }) => setInfo(data))
      .catch((e) => console.warn("landing: gagal memuat info publik", e?.message || e));
    api.get("/public/landing")
      .then(({ data }) => setLanding(data))
      .catch((e) => console.warn("landing: gagal memuat konten landing", e?.message || e));
    setCsTurn(pickCsTurn());
  }, []);

  const appName = landing?.app_name || info?.app_name || "ALPHA BILL";
  const plans = info?.plans || [];
  const logo = landing?.logo || "";
  const gallery = landing?.gallery || [];
  const testimonials = landing?.testimonials || [];

  const csList = useMemo(() => {
    const list = landing?.cs_numbers || [];
    if (list.length) return list;
    const fallback = info?.cs_wa || info?.wa_number || "";
    return fallback ? [{ name: "Customer Service", number: fallback }] : [];
  }, [landing, info]);

  const cs = csList.length ? csList[csTurn % csList.length] : null;
  const csHref = cs ? waLink(cs.number, `Halo ${cs.name || "CS"} ${appName}, saya ingin bertanya tentang Dashboard Billing ${appName}.`) : "";

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-background text-foreground" data-testid="landing-page">
      <Blobs />

      {/* ---------- Header ---------- */}
      <header className="sticky top-0 z-40 border-b border-white/5 bg-background/70 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <Brand logo={logo} appName={appName} />
            <div className="leading-none">
              <span className="font-display text-base font-semibold tracking-tight sm:text-lg">{appName}</span>
              <p className="mt-0.5 text-[9px] uppercase tracking-[0.22em] text-muted-foreground">Billing Dashboard</p>
            </div>
          </Link>
          <Button
            asChild
            className="h-9 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 px-4 text-xs font-medium text-white shadow-lg shadow-violet-600/20 transition-opacity duration-200 hover:opacity-90 sm:h-10 sm:px-5 sm:text-sm"
          >
            <Link to="/login" data-testid="landing-member-area">
              <LogIn className="mr-1.5 h-4 w-4" /> Member Area
            </Link>
          </Button>
        </div>
      </header>

      {/* ---------- Hero / Banner ---------- */}
      <section className="relative px-4 pb-16 pt-12 sm:px-6 sm:pt-16 lg:pb-24 lg:pt-20">
        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2 lg:gap-10">
          <div className="fade-up text-center lg:text-left">
            <span className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-cyan-300 sm:text-[11px]">
              <BadgeCheck className="h-3.5 w-3.5" /> RT-RW Net · RouterOS v7
            </span>

            <h1
              className="mt-5 font-display text-3xl font-semibold leading-[1.15] tracking-tight sm:text-4xl lg:text-[3.25rem]"
              data-testid="landing-headline"
            >
              Selamat datang di{" "}
              <span className="block sm:inline">Dashboard Billing</span>{" "}
              <span className="text-grad">{appName}</span>
            </h1>

            <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base lg:mx-0" data-testid="landing-tagline">
              Smart Dashboard yang mengelola Usaha Rt-RW Net dengan{" "}
              <span className="font-medium text-foreground">Rest API Mikrotik</span> yang mudah di gunakan namun sangat{" "}
              <span className="font-medium text-foreground">Power Full</span>.
            </p>

            <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row lg:justify-start">
              <Button
                asChild
                className="h-12 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 px-7 text-sm font-semibold text-white shadow-xl shadow-violet-600/25 transition-transform duration-200 hover:scale-[1.02] hover:opacity-95"
              >
                <Link to="/daftar" data-testid="landing-register-btn">
                  <Rocket className="mr-2 h-4 w-4" /> Registrasi Member
                </Link>
              </Button>
              <Button
                asChild variant="outline"
                className="h-12 rounded-full border-white/15 bg-white/5 px-7 text-sm text-foreground backdrop-blur transition-colors duration-200 hover:bg-white/10"
              >
                <Link to="/login" data-testid="landing-hero-login">
                  <LayoutDashboard className="mr-2 h-4 w-4" /> Masuk Dasbor
                </Link>
              </Button>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {STATS.map((s) => (
                <div key={s.label} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                  <s.icon className="mx-auto h-4 w-4 text-cyan-300 lg:mx-0" />
                  <p className="mt-2 font-display text-sm font-semibold">{s.value}</p>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="fade-up relative">
            <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-tr from-cyan-500/20 via-violet-600/20 to-fuchsia-600/10 blur-2xl" />
            <div className="relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-card/60 shadow-2xl">
              <img src={HERO_IMG} alt="Jaringan futuristik" className="h-56 w-full object-cover opacity-80 sm:h-72 lg:h-80" loading="eager" />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 grid grid-cols-3 gap-2">
                {[
                  { l: "Hotspot", v: "Aktif", i: Wifi },
                  { l: "PPPoE", v: "Online", i: Cpu },
                  { l: "Pendapatan", v: "Live", i: BarChart3 },
                ].map((c) => (
                  <div key={c.l} className="rounded-xl border border-white/10 bg-background/70 p-2 text-center backdrop-blur-md">
                    <c.i className="mx-auto h-3.5 w-3.5 text-violet-300" />
                    <p className="mt-1 text-[11px] font-semibold">{c.v}</p>
                    <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{c.l}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Fitur ---------- */}
      <section className="relative px-4 py-14 sm:px-6 lg:py-20" id="fitur">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground">Fitur</p>
            <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
              Semua kebutuhan billing <span className="text-grad">dalam satu dasbor</span>
            </h2>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br ${f.tone} p-5 backdrop-blur transition-transform duration-300 hover:-translate-y-1`}
              >
                <div className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-background/60">
                  <f.icon className={`h-5 w-5 ${f.ring}`} />
                </div>
                <h3 className="mt-4 font-display text-base font-semibold">{f.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground sm:text-[13px]">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Cara kerja ---------- */}
      <section className="relative px-4 py-14 sm:px-6 lg:py-20">
        <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-2">
          <div className="relative order-2 lg:order-1">
            <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-violet-600/20 to-cyan-500/10 blur-2xl" />
            <img src={RACK_IMG} alt="Perangkat jaringan" className="relative h-56 w-full rounded-[1.5rem] border border-white/10 object-cover opacity-85 sm:h-72" loading="lazy" />
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground">Cara Kerja</p>
            <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight sm:text-3xl">Tiga langkah, langsung jalan</h2>
            <div className="mt-7 space-y-4">
              {STEPS.map((s) => (
                <div key={s.n} className="flex gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur">
                  <span className="font-display text-xl font-semibold text-grad">{s.n}</span>
                  <div>
                    <h3 className="font-display text-sm font-semibold sm:text-base">{s.title}</h3>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground sm:text-[13px]">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Galeri screenshot dasbor ---------- */}
      {gallery.length > 0 && (
        <section className="relative px-4 py-14 sm:px-6 lg:py-20" data-testid="landing-gallery">
          <div className="mx-auto max-w-6xl">
            <div className="mx-auto max-w-2xl text-center">
              <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
                <Images className="h-3.5 w-3.5" /> Galeri Dasbor
              </p>
              <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
                Lihat langsung <span className="text-grad">tampilan aplikasinya</span>
              </h2>
              <p className="mt-3 text-xs text-muted-foreground sm:text-sm">Ketuk gambar untuk memperbesar.</p>
            </div>
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              {gallery.map((g, i) => (
                <button
                  key={`${g.title}-${i}`} type="button"
                  onClick={() => setLightbox(g)}
                  data-testid={`gallery-item-${i}`}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 bg-card/40 text-left transition-transform duration-300 hover:-translate-y-1"
                >
                  <img src={g.image} alt={g.title || `Screenshot ${i + 1}`} className="h-44 w-full object-cover object-top opacity-90 transition-opacity duration-300 group-hover:opacity-100 sm:h-56" loading="lazy" />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background to-transparent p-3">
                    <p className="font-display text-sm font-semibold">{g.title || `Tampilan ${i + 1}`}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------- Testimoni ---------- */}
      {testimonials.length > 0 && (
        <section className="relative px-4 py-14 sm:px-6 lg:py-20" data-testid="landing-testimonials">
          <div className="mx-auto max-w-6xl">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground">Testimoni</p>
              <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
                Kata <span className="text-grad">pengguna ALPHA BILL</span>
              </h2>
            </div>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {testimonials.map((t, i) => (
                <div
                  key={`${t.name}-${i}`}
                  data-testid={`testimonial-${i}`}
                  className="relative flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur transition-transform duration-300 hover:-translate-y-1"
                >
                  <Quote className="h-5 w-5 text-violet-300/70" />
                  <p className="mt-3 flex-1 text-xs leading-relaxed text-muted-foreground sm:text-[13px]">“{t.text}”</p>
                  <div className="mt-4 flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} className={`h-3.5 w-3.5 ${s <= (t.rating || 5) ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />
                    ))}
                  </div>
                  <div className="mt-4 flex items-center gap-3 border-t border-white/5 pt-4">
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-cyan-500/30 to-violet-600/30 font-display text-xs font-semibold">
                      {(t.name || "?").trim().charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-display text-sm font-semibold">{t.name}</p>
                      {t.city && <p className="truncate text-[11px] text-muted-foreground">{t.city}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------- Paket sewa ---------- */}
      {plans.length > 0 && (
        <section className="relative px-4 py-14 sm:px-6 lg:py-20" data-testid="landing-plans">
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground">Paket Sewa</p>
            <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight sm:text-3xl">Pilih paket sesuai skala usaha</h2>
            <div className="mt-9 grid gap-4 sm:grid-cols-2">
              {plans.map((p, i) => (
                <div
                  key={p.key}
                  className={`relative rounded-2xl border p-6 text-left backdrop-blur transition-transform duration-300 hover:-translate-y-1 ${
                    i === 1 ? "border-violet-500/40 bg-gradient-to-br from-violet-600/15 to-cyan-500/5" : "border-white/10 bg-white/[0.03]"
                  }`}
                >
                  {i === 1 && (
                    <span className="absolute right-4 top-4 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 px-2.5 py-0.5 text-[10px] font-semibold text-white">
                      Hemat
                    </span>
                  )}
                  <h3 className="font-display text-lg font-semibold">{p.label}</h3>
                  <p className="mt-3 font-display text-3xl font-semibold text-grad">{rp(p.price)}</p>
                  <p className="text-xs text-muted-foreground">/ {p.months} bulan</p>
                  <p className="mt-4 text-xs leading-relaxed text-muted-foreground">{p.desc}</p>
                  <ul className="mt-4 space-y-1.5">
                    {["Semua fitur dasbor", "Voucher & FTTH tanpa batas", "Update & dukungan CS"].map((t) => (
                      <li key={t} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Check className="h-3.5 w-3.5 text-emerald-400" /> {t}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------- CTA ---------- */}
      <section className="relative px-4 pb-20 sm:px-6">
        <div className="relative mx-auto max-w-5xl overflow-hidden rounded-[1.75rem] border border-white/10">
          <img src={DATA_IMG} alt="Data jaringan" className="absolute inset-0 h-full w-full object-cover opacity-25" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/50" />
          <div className="relative px-6 py-12 text-center sm:px-12 sm:py-16">
            <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">
              Siap membawa usaha RT-RW Net Anda <span className="text-grad">naik kelas?</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm text-muted-foreground">
              Daftar sebagai member sekarang, hubungkan router Anda, dan kelola semuanya dari satu dasbor — di laptop maupun di HP.
            </p>
            <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row">
              <Button
                asChild
                className="h-12 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 px-7 text-sm font-semibold text-white shadow-xl shadow-violet-600/25 hover:opacity-95"
              >
                <Link to="/daftar" data-testid="landing-register-btn-cta">
                  Registrasi Member <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild variant="outline"
                className="h-12 rounded-full border-white/15 bg-white/5 px-7 text-sm hover:bg-white/10"
              >
                <Link to="/login" data-testid="landing-cta-login">Member Area</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Footer ---------- */}
      <footer className="relative border-t border-white/5 px-4 py-8 sm:px-6">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 text-center sm:flex-row sm:text-left">
          <div className="flex items-center gap-2.5">
            <Brand logo={logo} appName={appName} size="h-8 w-8" icon="h-4 w-4" />
            <span className="font-display text-sm font-semibold">{appName}</span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Smart Dashboard Billing RT-RW Net · Mikrotik REST API · © {new Date().getFullYear()} {appName}
          </p>
          <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
            <Link to="/instal" className="transition-colors hover:text-violet-300">Pasang di HP</Link>
            <Link to="/login" className="transition-colors hover:text-violet-300">Masuk</Link>
          </div>
        </div>
      </footer>

      {/* ---------- Lightbox galeri ---------- */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-background/90 p-4 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <div className="relative max-h-full w-full max-w-5xl overflow-auto rounded-2xl border border-white/10 bg-card" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 border-b border-white/5 px-4 py-3">
              <p className="font-display text-sm font-semibold">{lightbox.title || "Tampilan dasbor"}</p>
              <button type="button" onClick={() => setLightbox(null)} data-testid="gallery-lightbox-close" className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground" aria-label="Tutup">
                <X className="h-4 w-4" />
              </button>
            </div>
            <img src={lightbox.image} alt={lightbox.title || "Screenshot dasbor"} className="w-full" />
          </div>
        </div>
      )}

      {/* ---------- Floating WA CS ---------- */}
      {csHref && (
        <a
          href={csHref} target="_blank" rel="noopener noreferrer"
          data-testid="landing-wa-float"
          aria-label={`Hubungi ${cs?.name || "Customer Service"} via WhatsApp`}
          className="group fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-[#25D366] py-3 pl-3 pr-3 text-white shadow-2xl shadow-emerald-600/30 transition-all duration-300 hover:pr-5 sm:bottom-6 sm:right-6"
        >
          <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-[#25D366]/40" />
          <MessageCircle className="h-6 w-6" />
          <span className="hidden text-sm font-semibold sm:inline-block sm:max-w-0 sm:overflow-hidden sm:whitespace-nowrap sm:transition-all sm:duration-300 sm:group-hover:max-w-[12rem]">
            {csList.length > 1 ? `Chat ${cs?.name || "CS"}` : "CS WhatsApp"}
          </span>
        </a>
      )}
    </div>
  );
}
